import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { DownloadIcon } from 'src/components/ui-icons/upload';
import { selectModelData, selectModelLoading } from '../../../selectors/selectors';
import { fetchData } from '../../../actions/actions';
import DeleteIcon from 'src/pages/DashboardPage/gos/ui-icons/deleteIcon';
import { BACKEND_URL } from '../../../api/config';

type FileWithProgress = {
  file: File;
  progress: number;
  isUploading: boolean;
  id: string;
  serverId?: number;
  serverPath?: string;
  fileName?: string;
  fileSize?: number;
};

type StrapiFileUploaderProps = {
  commentId?: number;
  onFilesChange?: (files: File[]) => void;
  disabled?: boolean;
  isEditMode?: boolean;
};

const StrapiFileUploader: React.FC<StrapiFileUploaderProps> = ({ commentId, onFilesChange, disabled = false, isEditMode = false }) => {
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hasLoadedExisting, setHasLoadedExisting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadsData = useAppSelector((state) => selectModelData(state, 'gop-uploads'));
  const isLoading = useAppSelector((state) => selectModelLoading(state, 'gop-uploads'));

  useEffect(() => {
    if (commentId && !hasLoadedExisting && !isEditMode) {
      dispatch(fetchData('gop-uploads', { CommentId: commentId }));
      setHasLoadedExisting(true);
    }
  }, [commentId, dispatch, hasLoadedExisting, isEditMode]);

  useEffect(() => {
    setHasLoadedExisting(false);
    setFiles([]);
  }, [commentId]);

  const existingFiles = useMemo(() => {
    if (!commentId || !uploadsData.length || isEditMode) return [];

    return uploadsData
      .filter((upload: any) => upload.CommentId === commentId)
      .map((upload: any) => ({
        file: new File([new Blob()], upload.Docs?.name || 'file', {
          type: 'application/octet-stream',
        }),
        progress: 100,
        isUploading: false,
        id: `existing_${upload.id}`,
        serverId: upload.id,
        serverPath: upload.Docs?.url,
        fileName: upload.Docs?.name,
        fileSize: upload.Docs?.size,
      }));
  }, [commentId, uploadsData, isEditMode]);

  useEffect(() => {
    if (existingFiles.length > 0 && !isEditMode) {
      setFiles((prevFiles) => {
        const newFiles = prevFiles.filter((f) => !f.id.startsWith('existing_'));
        return [...existingFiles, ...newFiles];
      });
    }
  }, [existingFiles, isEditMode]);

  const newFiles = useMemo(() => {
    if (isEditMode) {
      return files.map((f) => f.file);
    } else {
      return files.filter((f) => !f.id.startsWith('existing_')).map((f) => f.file);
    }
  }, [files, isEditMode]);

  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(newFiles);
    }
  }, [onFilesChange, newFiles]);

  const handleFiles = useCallback(
    (inputFiles: FileList) => {
      if (disabled) return;

      const newFilesToAdd = Array.from(inputFiles)
        .map((file, index) => {
          if (file.size > 50 * 1024 * 1024) {
            alert(`Файл ${file.name} превышает лимит в 50MB`);
            return null;
          }

          return {
            file,
            progress: 0,
            isUploading: false,
            id: `new_${file.name}_${Date.now()}_${index}`,
            fileName: file.name,
            fileSize: file.size,
          };
        })
        .filter(Boolean) as FileWithProgress[];

      if (newFilesToAdd.length > 0) {
        setFiles((prev) => [...prev, ...newFilesToAdd]);
      }
    },
    [disabled]
  );

  const handleDelete = useCallback(
    async (fileInfo: FileWithProgress) => {
      if (disabled) return;

      try {
        if (fileInfo.serverId) {
          // dispatch(delete(fileInfo.serverId));
          console.log('Удаление', fileInfo.serverId);
        }
        setFiles((prev) => prev.filter((f) => f.id !== fileInfo.id));
      } catch (error) {
        console.error('Ошибка при удалении файла:', error);
      }
    },
    [disabled]
  );

  const handleDownload = useCallback(async (fileInfo: FileWithProgress) => {
    try {
      if (fileInfo.serverPath) {
        const fullUrl = fileInfo.serverPath.startsWith('http') ? fileInfo.serverPath : `${BACKEND_URL}${fileInfo.serverPath}`;
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = fileInfo.fileName || 'downloaded-file';
        link.click();
      } else if (fileInfo.file) {
        const blobUrl = URL.createObjectURL(fileInfo.file);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileInfo.file.name;
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        console.warn('Файл не найден для скачивания');
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!disabled && e.dataTransfer.files) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, handleFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles]
  );

  const handleSelectFiles = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  return (
    <div className='w-full'>
      <label className='block text-sm font-medium text-gray-700 mb-2'>Файлы</label>

      <div
        className={`w-full h-60 bg-[#F3F4F6] border border-dashed rounded-lg flex flex-col items-center justify-center
        ${isDragging ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input type='file' multiple className='hidden' ref={fileInputRef} onChange={handleFileInputChange} disabled={disabled} />

        <DocumentIcon className='w-6 h-6 text-[#637381] mb-2' />
        <div className='text-lg text-[#637381]'>
          Перетащите документ сюда или{' '}
          <button type='button' onClick={handleSelectFiles} className={`${disabled ? 'text-gray-400' : 'text-blue-900 hover:text-blue-700'}`} disabled={disabled}>
            выберите файл
          </button>
        </div>
        <p className='text-sm text-[#637381] mt-1'>Размер файла не более 50MB</p>
      </div>
      {files.length > 0 && (
        <div className='mt-4 space-y-4'>
          {files.map((fileInfo) => (
            <div key={fileInfo.id} className='flex flex-col p-3 bg-[#F3F4F6] rounded-lg'>
              <div className='flex items-center justify-between cursor-pointer hover:bg-gray-100 rounded p-2 -m-2' onClick={() => handleDownload(fileInfo)} title='Нажмите для скачивания файла'>
                <div className='flex items-center flex-1 min-w-0 mr-4'>
                  <DocumentIcon className='w-5 h-5 text-slate-400 mr-3 flex-shrink-0' />
                  <div className='truncate'>
                    <span className='text-base text-[#1F2A37]'>{fileInfo.fileName || fileInfo.file.name}</span>
                    <span className='text-base text-[#1F2A37] ml-2'>({fileInfo.fileSize ? (fileInfo.fileSize / 1024 / 1024).toFixed(2) : (fileInfo.file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    {fileInfo.id.startsWith('existing_') && <span className='text-sm text-gray-500 ml-2'>(загружен)</span>}
                  </div>
                </div>

                <div className='flex items-center gap-4 flex-shrink-0' onClick={(e) => e.stopPropagation()}>
                  {!fileInfo.isUploading && (
                    <button type='button' onClick={() => handleDownload(fileInfo)} className='p-1 hover:bg-gray-200 rounded' title='Скачать файл'>
                      <DownloadIcon />
                    </button>
                  )}
                  <button type='button' onClick={() => handleDelete(fileInfo)} className='p-1 hover:bg-gray-200 rounded' title='Удалить файл' disabled={disabled}>
                    <DeleteIcon />
                  </button>
                </div>
              </div>

              {fileInfo.isUploading && (
                <div className='w-full mt-2 h-1 bg-slate-200 rounded-full'>
                  <div className='h-1 bg-blue-600 rounded-full transition-all' style={{ width: `${fileInfo.progress}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StrapiFileUploader;
